import { useState, useEffect } from 'react';
import { getFunctions } from 'firebase/functions';
import { httpsCallable } from '../lib/callable';
import { useAuth } from '../contexts/AuthContext';
import { CLOUD_FUNCTIONS } from '../constants/firebase';
import { useToast } from '../contexts/ToastContext';
import {
  formatProjectLimit,
  parseProjectLimitInput,
  isProjectLimitUnchanged,
} from '../features/admin/projectLimitEditor';
import {
  defaultOrgChoice,
  evaluateOrgChoice,
  organisationCell,
  organisationLabel,
  selectableOrgs,
  type OrgOption,
} from '../features/admin/orgAssignment';
import OrganisationPicker from '../components/OrganisationPicker';
import { Link } from 'react-router-dom';
import { 
  Users, 
  CheckCircle, 
  XCircle, 
  Clock, 
  ArrowLeft,
  Shield,
  RefreshCw,
  Search,
  UserCheck,
  UserPlus,
  Trash2,
  X,
  Building2,
} from 'lucide-react';

interface UserRecord {
  id: string;
  uid: string;
  email: string;
  displayName: string;
  status: 'pending' | 'approved' | 'rejected';
  role: string;
  createdAt: { _seconds: number };
  approvedBy?: string;
  approvedAt?: { _seconds: number };
  /** Per-account project limit. Absent means the default (1). */
  projectLimit?: number;
  mfaRequired?: boolean;
  mfaEnrolled?: boolean;
  /** Display mirror of the authoritative Firebase Authentication claim. */
  superAdmin?: boolean;
  /** The account's organisation (users/{uid}.orgId). Absent means none. */
  orgId?: string;
  /** The orgId in the account's sign-in claim (from getUsersV2), or null. */
  claimOrgId?: string | null;
  /** Whether organizations/{orgId}/members/{uid} exists (from getUsersV2). */
  memberRowInUserOrg?: boolean;
}

/**
 * The organisation dialog. 'approve' approves an account and needs an
 * organisation to do it; 'fix-up' places an approved account that has none.
 */
interface OrgDialogState {
  mode: 'approve' | 'fix-up' | 'finish';
  user: UserRecord;
  role?: string;
}

export default function AdminUsers() {
  const { user, isSuperAdmin } = useAuth();
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const { showToast } = useToast();
  const [updating, setUpdating] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newUser, setNewUser] = useState({ displayName: '', email: '', role: 'editor', superAdmin: false });
  // Organisation for a new account. Until the administrator picks one, it
  // follows the email address (preselected when exactly one active
  // organisation's domains include it).
  const [newUserOrg, setNewUserOrg] = useState('');
  const [newUserOrgTouched, setNewUserOrgTouched] = useState(false);
  const [newUserOrgAck, setNewUserOrgAck] = useState(false);

  const [orgs, setOrgs] = useState<OrgOption[]>([]);
  const [orgsLoading, setOrgsLoading] = useState(true);
  const [orgDialog, setOrgDialog] = useState<OrgDialogState | null>(null);
  const [orgChoice, setOrgChoice] = useState('');
  const [orgAck, setOrgAck] = useState(false);
  const [orgSaving, setOrgSaving] = useState(false);

  const functions = getFunctions();

  const loadUsers = async () => {
    setLoading(true);
    try {
      const getUsers = httpsCallable(functions, CLOUD_FUNCTIONS.GET_USERS);
      const result = await getUsers({});
      setUsers(result.data as UserRecord[]);
    } catch (err: unknown) {
      const error = err as { message?: string };
      showToast(error.message || 'Failed to load users', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadOrgs = async () => {
    setOrgsLoading(true);
    try {
      const listOrganizations = httpsCallable(functions, CLOUD_FUNCTIONS.LIST_ORGANIZATIONS);
      const result = await listOrganizations({});
      setOrgs((result.data as { organizations: OrgOption[] }).organizations || []);
    } catch {
      showToast('Could not load the list of organisations. Refresh the page to try again.', 'error');
    } finally {
      setOrgsLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
    loadOrgs();
  }, []);

  const openOrgDialog = (state: OrgDialogState) => {
    // An organisation already stamped on the account (by the sign-up domain
    // match) is the starting choice when it is still selectable; otherwise
    // preselect only when exactly one active organisation matches the email.
    const stamped = state.user.orgId && selectableOrgs(orgs).some((o) => o.orgId === state.user.orgId)
      ? state.user.orgId
      : '';
    setOrgChoice(stamped || defaultOrgChoice(state.user.email, orgs));
    setOrgAck(false);
    setOrgDialog(state);
  };

  const orgDialogVerdict = orgDialog
    ? evaluateOrgChoice({ email: orgDialog.user.email, orgId: orgChoice, orgs, acknowledged: orgAck })
    : null;

  const submitOrgDialog = async () => {
    if (!orgDialog || !orgDialogVerdict?.canSubmit) return;
    const { mode, user: target, role } = orgDialog;
    const orgName = organisationLabel(orgChoice, orgs);
    setOrgSaving(true);
    setUpdating(target.id);
    try {
      if (mode === 'approve') {
        const updateUserStatus = httpsCallable(functions, CLOUD_FUNCTIONS.UPDATE_USER_STATUS);
        await updateUserStatus({
          userId: target.id,
          status: 'approved',
          role,
          orgId: orgChoice,
          overrideDomainMismatch: orgDialogVerdict.overrideDomainMismatch,
        });
        showToast(`${target.displayName || target.email} is approved and added to ${orgName}.`, 'success');
      } else {
        const assignOrganization = httpsCallable(functions, CLOUD_FUNCTIONS.ASSIGN_ACCOUNT_ORGANIZATION);
        await assignOrganization({
          userId: target.id,
          orgId: orgChoice,
          overrideDomainMismatch: orgDialogVerdict.overrideDomainMismatch,
        });
        showToast(
          `${target.displayName || target.email} is now in ${orgName}. They need to sign out and back in to see its studies.`,
          'success',
        );
      }
      setOrgDialog(null);
      await loadUsers();
    } catch (err: unknown) {
      const error = err as { message?: string };
      showToast(error.message || 'Could not save the organisation.', 'error');
    } finally {
      setOrgSaving(false);
      setUpdating(null);
    }
  };

  const updateStatus = async (userId: string, status: string, role?: string) => {
    setUpdating(userId);
    try {
      const updateUserStatus = httpsCallable(functions, CLOUD_FUNCTIONS.UPDATE_USER_STATUS);
      await updateUserStatus({ userId, status, role });
      await loadUsers();
    } catch (err: unknown) {
      const error = err as { message?: string };
      showToast(error.message || 'Failed to update user', 'error');
    } finally {
      setUpdating(null);
    }
  };

  const newUserOrgChoice = newUserOrgTouched ? newUserOrg : defaultOrgChoice(newUser.email, orgs);
  const newUserOrgVerdict = evaluateOrgChoice({
    email: newUser.email,
    orgId: newUserOrgChoice,
    orgs,
    acknowledged: newUserOrgAck,
  });

  const closeCreate = () => {
    setShowCreate(false);
    setNewUserOrg('');
    setNewUserOrgTouched(false);
    setNewUserOrgAck(false);
  };

  const createUser = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!newUserOrgVerdict.canSubmit) {
      showToast('Choose an organisation for this person before creating the account.', 'error');
      return;
    }
    setCreating(true);
    try {
      const createManagedUser = httpsCallable(functions, CLOUD_FUNCTIONS.CREATE_MANAGED_USER);
      await createManagedUser({
        ...newUser,
        orgId: newUserOrgChoice,
        overrideDomainMismatch: newUserOrgVerdict.overrideDomainMismatch,
        baseUrl: window.location.origin,
      });
      closeCreate();
      setNewUser({ displayName: '', email: '', role: 'editor', superAdmin: false });
      await loadUsers();
      showToast('User created. Temporary sign-in details were emailed.', 'success');
    } catch (err: unknown) {
      const error = err as { message?: string };
      showToast(error.message || 'Could not create the user', 'error');
    } finally {
      setCreating(false);
    }
  };

  const updateRole = async (u: UserRecord, role: string) => {
    setUpdating(u.id);
    try {
      const updateManagedUserRole = httpsCallable(functions, CLOUD_FUNCTIONS.UPDATE_MANAGED_USER_ROLE);
      await updateManagedUserRole({ userId: u.id, role });
      await loadUsers();
      showToast(`${u.displayName || u.email} is now ${role}.`, 'success');
    } catch (err: unknown) {
      const error = err as { message?: string };
      showToast(error.message || 'Could not update the role', 'error');
    } finally {
      setUpdating(null);
    }
  };

  const deleteUser = async (u: UserRecord) => {
    if (!window.confirm(`Delete ${u.displayName || u.email}? This permanently removes their sign-in account.`)) return;
    setUpdating(u.id);
    try {
      const deleteManagedUser = httpsCallable(functions, CLOUD_FUNCTIONS.DELETE_MANAGED_USER);
      await deleteManagedUser({ userId: u.id });
      await loadUsers();
      showToast('User deleted.', 'success');
    } catch (err: unknown) {
      const error = err as { message?: string };
      showToast(error.message || 'Could not delete the user', 'error');
    } finally {
      setUpdating(null);
    }
  };

  const updateMfaRequirement = async (u: UserRecord, required: boolean) => {
    setUpdating(u.id);
    try {
      const updateRequirement = httpsCallable(functions, CLOUD_FUNCTIONS.UPDATE_MANAGED_USER_MFA_REQUIREMENT);
      await updateRequirement({ userId: u.id, required });
      await loadUsers();
      showToast(required ? 'Authenticator enrollment is now required.' : 'Authenticator requirement removed.', 'success');
    } catch (err: unknown) {
      showToast((err as { message?: string }).message || 'Could not update MFA requirement', 'error');
    } finally { setUpdating(null); }
  };

  const updateSuperAdmin = async (u: UserRecord, enabled: boolean) => {
    const action = enabled ? 'grant super administrator access to' : 'remove super administrator access from';
    if (!window.confirm(`Are you sure you want to ${action} ${u.displayName || u.email}?`)) return;
    setUpdating(u.id);
    try {
      const updateAccess = httpsCallable(functions, CLOUD_FUNCTIONS.UPDATE_MANAGED_USER_SUPER_ADMIN);
      await updateAccess({ userId: u.id, enabled });
      await loadUsers();
      showToast(
        enabled
          ? 'Super administrator access granted. The user must sign out and back in.'
          : 'Super administrator access removed. The user must sign out and back in.',
        'success',
      );
    } catch (err: unknown) {
      showToast((err as { message?: string }).message || 'Could not update super administrator access', 'error');
    } finally {
      setUpdating(null);
    }
  };

  // Project limit editing. `editingLimit` holds the uid being edited so only
  // one row is in edit mode at a time; `limitDraft` is the raw text, validated
  // on submit by the same rules the server applies.
  const [editingLimit, setEditingLimit] = useState<string | null>(null);
  const [limitDraft, setLimitDraft] = useState('');

  const beginEditLimit = (u: UserRecord) => {
    setEditingLimit(u.id);
    setLimitDraft(String(u.projectLimit ?? 1));
  };

  const cancelEditLimit = () => {
    setEditingLimit(null);
    setLimitDraft('');
  };

  const submitLimit = async (u: UserRecord) => {
    const parsed = parseProjectLimitInput(limitDraft);
    if (!parsed.ok) {
      showToast(parsed.reason, 'error');
      return;
    }
    // Skip a write that changes nothing — it would still stamp
    // projectLimitSetBy/At, recording a change that did not happen.
    if (isProjectLimitUnchanged(u.projectLimit, parsed.value)) {
      cancelEditLimit();
      return;
    }
    setUpdating(u.id);
    try {
      const setProjectLimit = httpsCallable(functions, CLOUD_FUNCTIONS.SET_PROJECT_LIMIT);
      await setProjectLimit({ userId: u.id, projectLimit: parsed.value });
      cancelEditLimit();
      await loadUsers();
      showToast(`Project limit set to ${parsed.value}.`, 'success');
    } catch {
      // No raw server prose on screen; the field-level validation above
      // already covers the cases a person can actually cause.
      showToast('Could not update the project limit. Please try again.', 'error');
    } finally {
      setUpdating(null);
    }
  };

  const formatDate = (timestamp: { _seconds: number }) => {
    if (!timestamp?._seconds) return 'N/A';
    return new Date(timestamp._seconds * 1000).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'approved':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
            <CheckCircle className="w-3 h-3" />
            Approved
          </span>
        );
      case 'rejected':
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
            <XCircle className="w-3 h-3" />
            Rejected
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-medium">
            <Clock className="w-3 h-3" />
            Pending
          </span>
        );
    }
  };

  const pendingUsers = users.filter(u => u.status === 'pending');
  const approvedUsers = users.filter(u => u.status === 'approved');
  const filteredUsers = users.filter((u) => {
    const needle = search.trim().toLowerCase();
    if (needle && !`${u.displayName} ${u.email}`.toLowerCase().includes(needle)) return false;
    if (roleFilter !== 'all' && (u.role || 'viewer') !== roleFilter) return false;
    if (statusFilter !== 'all' && u.status !== statusFilter) return false;
    return true;
  });
  // const otherUsers = users.filter(u => u.status !== 'pending');

  return (
    <div className="min-h-screen bg-[#F7F9FB]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
      <header className="border-b border-[#DCE2E5] bg-white/90">
        <div className="mx-auto flex max-w-[1440px] items-center justify-between px-6 py-7 lg:px-10">
          <div className="flex items-center gap-4">
            <Link to="/" className="text-gray-500 hover:text-gray-700">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-2">
              <Shield className="h-6 w-6 text-[var(--brand-primary)]" />
              <div>
                <p className="mb-1 text-xs font-bold uppercase tracking-[0.14em] text-[var(--brand-primary)]">Access governance</p>
                <h1 className="text-3xl font-normal tracking-[-0.035em] text-[#001820]">User Management</h1>
                <p className="mt-1 text-sm text-[#5F696C]">Review access, roles, and account project limits.</p>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={loadUsers}
              disabled={loading}
              className="flex items-center gap-2 rounded-full border border-[#CAD2D5] bg-white px-4 py-2.5 text-sm font-semibold text-[#35565C] transition hover:border-[var(--brand-primary)]"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 rounded-full bg-[var(--brand-primary)] px-5 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-[#005557]">
              <UserPlus className="h-4 w-4" /> Add user
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-[1440px] px-6 py-8 lg:px-10">

        <div className="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="rounded-2xl border border-[#DCE2E5] bg-white p-5 shadow-[0_10px_30px_rgba(15,42,50,0.05)]">
            <Users className="mb-3 h-5 w-5 text-[var(--brand-primary)]" />
            <p className="text-3xl font-normal text-[#001820]">{loading ? '—' : users.length}</p>
            <p className="text-sm text-[#5F696C]">Total users</p>
          </div>
          <div className="rounded-2xl border border-[#DCE2E5] bg-white p-5 shadow-[0_10px_30px_rgba(15,42,50,0.05)]">
            <UserCheck className="mb-3 h-5 w-5 text-[#16784B]" />
            <p className="text-3xl font-normal text-[#001820]">{loading ? '—' : approvedUsers.length}</p>
            <p className="text-sm text-[#5F696C]">Approved</p>
          </div>
          <div className="rounded-2xl border border-[#DCE2E5] bg-white p-5 shadow-[0_10px_30px_rgba(15,42,50,0.05)]">
            <Clock className="mb-3 h-5 w-5 text-[#B46B08]" />
            <p className="text-3xl font-normal text-[#001820]">{loading ? '—' : pendingUsers.length}</p>
            <p className="text-sm text-[#5F696C]">Awaiting review</p>
          </div>
        </div>

        {/* Pending Approvals */}
        {pendingUsers.length > 0 && (
          <div className="mb-8">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Clock className="w-5 h-5 text-yellow-600" />
              Pending Approvals ({pendingUsers.length})
            </h2>
            <div className="overflow-x-auto rounded-2xl border border-amber-200 bg-white shadow-[0_10px_30px_rgba(15,42,50,0.06)]">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Signed Up</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Authenticator</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {pendingUsers.map((u) => (
                    <tr key={u.id} className="hover:bg-gray-50">
                      <td className="px-4 py-4">
                        <div>
                          <div className="font-medium text-gray-900">{u.displayName}</div>
                          <div className="text-sm text-gray-500">{u.email}</div>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-500">
                        {formatDate(u.createdAt)}
                      </td>
                      <td className="px-4 py-4">
                        {getStatusBadge(u.status)}
                      </td>
                      <td className="px-4 py-4">
                        <select value={u.mfaRequired ? 'required' : 'optional'} onChange={(e) => updateMfaRequirement(u, e.target.value === 'required')} disabled={updating === u.id} className="rounded-lg border border-[#CAD2D5] bg-white px-3 py-2 text-sm font-semibold text-[#35565C]">
                          <option value="optional">Optional</option>
                          <option value="required">Required</option>
                        </select>
                        <p className={`mt-1 text-xs ${u.mfaEnrolled ? 'text-emerald-700' : 'text-[#7B898D]'}`}>{u.mfaEnrolled ? 'Enrolled' : u.mfaRequired ? 'Enrollment pending' : 'Not enrolled'}</p>
                      </td>
                      <td className="px-4 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => openOrgDialog({ mode: 'approve', user: u, role: 'editor' })}
                            disabled={updating === u.id}
                            className="rounded-full bg-[var(--brand-primary)] px-4 py-2 text-sm font-bold text-white hover:bg-[#005557] disabled:opacity-50"
                          >
                            {updating === u.id ? 'Updating...' : 'Approve'}
                          </button>
                          <button
                            onClick={() => updateStatus(u.id, 'rejected')}
                            disabled={updating === u.id}
                            className="rounded-full border border-red-200 px-4 py-2 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
                          >
                            Reject
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* All Users */}
        <div>
          <div className="mb-4 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-[#001820]">
            <Users className="h-5 w-5 text-[var(--brand-primary)]" />
            {/* The count is rendered ONLY once it is known. Previously this read
                "All Users (0)" while the fetch was still in flight, which on a
                cold start meant ~5 seconds asserting there were no users when
                the request had not yet returned. An unknown count must look
                unknown, not like zero. */}
            All Users{!loading && ` (${filteredUsers.length})`}
          </h2>
          <div className="flex flex-col gap-2 sm:flex-row">
            <label className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#7B898D]" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name or email"
                className="h-10 w-full rounded-xl border border-[#CAD2D5] bg-white pl-9 pr-3 text-sm outline-none focus:border-[var(--brand-primary)] focus:ring-4 focus:ring-[var(--brand-primary)]/10 sm:w-64"
              />
            </label>
            <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} className="h-10 rounded-xl border border-[#CAD2D5] bg-white px-3 text-sm text-[#35565C]">
              <option value="all">All roles</option><option value="admin">Admin</option><option value="editor">Editor</option><option value="viewer">Viewer</option>
            </select>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-10 rounded-xl border border-[#CAD2D5] bg-white px-3 text-sm text-[#35565C]">
              <option value="all">All statuses</option><option value="approved">Approved</option><option value="pending">Pending</option><option value="rejected">Rejected</option>
            </select>
          </div>
          </div>
          
          {loading ? (
            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
              Loading users...
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
              No users found
            </div>
          ) : (
            <div className="overflow-x-auto rounded-2xl border border-[#DCE2E5] bg-white shadow-[0_10px_30px_rgba(15,42,50,0.06)]">
              <table className="w-full min-w-[1080px]">
                <thead className="border-b border-[#DCE2E5] bg-[#F7F9FB]">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Organisation</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role & access</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Authenticator</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Project Limit</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Signed Up</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {filteredUsers.map((u) => (
                    <tr key={u.id} className={`transition hover:bg-[#F7FBFA] ${u.uid === user?.uid ? 'bg-[#EAF6F4]' : ''}`}>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-3">
                          <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-[#E6F4F2] text-sm font-bold text-[var(--brand-primary)]">
                            {(u.displayName || u.email || '?').charAt(0).toUpperCase()}
                          </div>
                          <div>
                          <div className="font-medium text-gray-900">
                            {u.displayName}
                            {u.uid === user?.uid && (
                              <span className="ml-2 text-xs text-blue-600">(You)</span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">{u.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 text-sm">
                        {(() => {
                          const cell = organisationCell(u, orgs);
                          if (cell.kind === 'member') {
                            return <span className="font-semibold text-[#35565C]">{cell.label}</span>;
                          }
                          if (cell.kind === 'suggested') {
                            return (
                              <div className="flex flex-col items-start gap-2">
                                <span className="text-[#7B898D]">{cell.label}</span>
                                {u.status === 'approved' && (
                                  <button
                                    onClick={() => openOrgDialog({ mode: 'fix-up', user: u })}
                                    disabled={updating === u.id}
                                    className="inline-flex items-center gap-1 rounded-lg border border-[#CAD2D5] px-3 py-1.5 text-xs font-semibold text-[#35565C] hover:border-[var(--brand-primary)] hover:text-[var(--brand-primary)] disabled:opacity-50"
                                  >
                                    <Building2 className="h-3 w-3" /> Choose organisation
                                  </button>
                                )}
                              </div>
                            );
                          }
                          if (cell.kind === 'needs-finish') {
                            return (
                              <div className="flex flex-col items-start gap-2">
                                <span className="font-semibold text-[#35565C]">{cell.label}</span>
                                <span className="text-xs text-amber-800">This account is a member, but its sign-in was not updated, so it cannot see the organisation's studies yet.</span>
                                <button
                                  onClick={() => openOrgDialog({ mode: 'finish', user: u })}
                                  disabled={updating === u.id}
                                  className="inline-flex items-center gap-1 rounded-lg border border-amber-300 px-3 py-1.5 text-xs font-semibold text-amber-900 hover:bg-amber-50 disabled:opacity-50"
                                >
                                  <Building2 className="h-3 w-3" /> Finish organisation setup
                                </button>
                              </div>
                            );
                          }
                          return null;
                        })()}
                        {!u.orgId && !u.claimOrgId && (
                          <div className="flex flex-col items-start gap-2">
                            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-800">
                              No organisation
                            </span>
                            {u.status === 'approved' && (
                              <button
                                onClick={() => openOrgDialog({ mode: 'fix-up', user: u })}
                                disabled={updating === u.id}
                                className="inline-flex items-center gap-1 rounded-lg border border-[#CAD2D5] px-3 py-1.5 text-xs font-semibold text-[#35565C] hover:border-[var(--brand-primary)] hover:text-[var(--brand-primary)] disabled:opacity-50"
                              >
                                <Building2 className="h-3 w-3" /> Choose organisation
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        {u.superAdmin && (
                          <span className="mb-2 inline-flex items-center gap-1 rounded-full bg-violet-100 px-2 py-1 text-xs font-semibold text-violet-700">
                            <Shield className="h-3 w-3" /> Super admin
                          </span>
                        )}
                        <select
                          value={u.role || 'viewer'}
                          onChange={(e) => updateRole(u, e.target.value)}
                          disabled={updating === u.id || u.uid === user?.uid || u.superAdmin}
                          aria-label={`Role for ${u.email}`}
                          className="rounded-lg border border-[#CAD2D5] bg-white px-3 py-2 text-sm font-semibold text-[#35565C] disabled:cursor-not-allowed disabled:bg-[#F1F4F5]"
                        >
                          <option value="admin">Admin</option>
                          <option value="editor">Editor</option>
                          <option value="viewer">Viewer</option>
                        </select>
                      </td>
                      <td className="px-4 py-4">
                        {getStatusBadge(u.status)}
                      </td>
                      <td className="px-4 py-4">
                        <select value={u.mfaRequired ? 'required' : 'optional'} onChange={(e) => updateMfaRequirement(u, e.target.value === 'required')} disabled={updating === u.id} className="rounded-lg border border-[#CAD2D5] bg-white px-3 py-2 text-sm font-semibold text-[#35565C]">
                          <option value="optional">Optional</option>
                          <option value="required">Required</option>
                        </select>
                        <p className={`mt-1 text-xs ${u.mfaEnrolled ? 'text-emerald-700' : 'text-[#7B898D]'}`}>{u.mfaEnrolled ? 'Enrolled' : u.mfaRequired ? 'Enrollment pending' : 'Not enrolled'}</p>
                      </td>
                      <td className="px-4 py-4 text-sm">
                        {u.role === 'admin' ? (
                          // Admins are exempt server-side, so a number here
                          // would be a value that does nothing.
                          <span className="text-gray-400">Exempt</span>
                        ) : editingLimit === u.id ? (
                          <div className="flex items-center gap-1">
                            <input
                              type="text"
                              inputMode="numeric"
                              value={limitDraft}
                              onChange={(e) => setLimitDraft(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') submitLimit(u);
                                if (e.key === 'Escape') cancelEditLimit();
                              }}
                              autoFocus
                              disabled={updating === u.id}
                              className="w-16 text-sm border rounded px-2 py-1"
                              aria-label={`Project limit for ${u.email}`}
                            />
                            <button
                              onClick={() => submitLimit(u)}
                              disabled={updating === u.id}
                              className="rounded bg-[var(--brand-primary)] px-2 py-1 text-xs text-white hover:bg-[#005557] disabled:opacity-50"
                            >
                              {updating === u.id ? '…' : 'Save'}
                            </button>
                            <button
                              onClick={cancelEditLimit}
                              disabled={updating === u.id}
                              className="px-2 py-1 text-xs text-gray-600 hover:text-gray-900"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => beginEditLimit(u)}
                            className="font-semibold text-[#35565C] hover:text-[var(--brand-primary)] hover:underline"
                            title="Change this account's project limit"
                          >
                            {formatProjectLimit(u.projectLimit)}
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-500">
                        {formatDate(u.createdAt)}
                      </td>
                      <td className="px-4 py-4 text-right">
                        {u.uid !== user?.uid && (
                          <div className="flex items-center justify-end gap-2">
                            {u.status !== 'pending' && !u.superAdmin && (
                              <select
                                value={u.status}
                                onChange={(e) => {
                                  // Approval needs an organisation, so it always
                                  // goes through the dialog (which starts from
                                  // the account's organisation if it has one).
                                  if (e.target.value === 'approved') {
                                    openOrgDialog({ mode: 'approve', user: u, role: u.role });
                                    return;
                                  }
                                  updateStatus(u.id, e.target.value, u.role);
                                }}
                                disabled={updating === u.id}
                                className="rounded-lg border border-[#CAD2D5] bg-white px-3 py-2 text-sm"
                              >
                                <option value="approved">Approved</option>
                                <option value="rejected">Rejected</option>
                                <option value="pending">Pending</option>
                              </select>
                            )}
                            {isSuperAdmin && (
                              <button
                                onClick={() => updateSuperAdmin(u, !u.superAdmin)}
                                disabled={updating === u.id}
                                className={`rounded-lg border px-3 py-2 text-xs font-semibold transition disabled:opacity-50 ${u.superAdmin ? 'border-violet-200 text-violet-700 hover:bg-violet-50' : 'border-[#CAD2D5] text-[#35565C] hover:border-violet-300 hover:text-violet-700'}`}
                              >
                                {u.superAdmin ? 'Remove super admin' : 'Make super admin'}
                              </button>
                            )}
                            {!u.superAdmin && (
                              <button
                                onClick={() => deleteUser(u)}
                                disabled={updating === u.id}
                                title={`Delete ${u.displayName || u.email}`}
                                className="rounded-lg border border-red-200 p-2 text-red-600 transition hover:bg-red-50 disabled:opacity-50"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#001820]/55 p-4" role="dialog" aria-modal="true" aria-labelledby="create-user-title">
          <form onSubmit={createUser} className="w-full max-w-lg rounded-3xl bg-white p-7 shadow-2xl">
            <div className="mb-6 flex items-start justify-between">
              <div>
                <p className="mb-1 text-xs font-bold uppercase tracking-[0.14em] text-[var(--brand-primary)]">Account provisioning</p>
                <h2 id="create-user-title" className="text-2xl font-semibold text-[#001820]">Add a user</h2>
                <p className="mt-2 text-sm leading-6 text-[#5F696C]">A secure temporary password will be generated and emailed to the user.</p>
              </div>
              <button type="button" onClick={closeCreate} className="rounded-full p-2 text-[#5F696C] hover:bg-[#F1F4F5]" aria-label="Close">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4">
              <label className="block text-sm font-semibold text-[#274249]">Full name
                <input required value={newUser.displayName} onChange={(e) => setNewUser({ ...newUser, displayName: e.target.value })} className="mt-2 h-11 w-full rounded-xl border border-[#CAD2D5] px-3 font-normal outline-none focus:border-[var(--brand-primary)] focus:ring-4 focus:ring-[var(--brand-primary)]/10" placeholder="Jane Smith" />
              </label>
              <label className="block text-sm font-semibold text-[#274249]">Email address
                <input required type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} className="mt-2 h-11 w-full rounded-xl border border-[#CAD2D5] px-3 font-normal outline-none focus:border-[var(--brand-primary)] focus:ring-4 focus:ring-[var(--brand-primary)]/10" placeholder="jane@example.com" />
              </label>
              <label className="block text-sm font-semibold text-[#274249]">Role
                <select value={newUser.role} disabled={newUser.superAdmin} onChange={(e) => setNewUser({ ...newUser, role: e.target.value })} className="mt-2 h-11 w-full rounded-xl border border-[#CAD2D5] bg-white px-3 font-normal outline-none focus:border-[var(--brand-primary)] disabled:bg-[#F1F4F5]">
                  <option value="admin">Admin — manage users and all projects</option>
                  <option value="editor">Editor — create and edit projects</option>
                  <option value="viewer">Viewer — read-only access</option>
                </select>
              </label>
              <OrganisationPicker
                email={newUser.email}
                orgs={orgs}
                orgsLoading={orgsLoading}
                value={newUserOrgChoice}
                onChange={(orgId) => {
                  setNewUserOrg(orgId);
                  setNewUserOrgTouched(true);
                }}
                acknowledged={newUserOrgAck}
                onAcknowledgedChange={setNewUserOrgAck}
                disabled={creating}
              />
              {isSuperAdmin && (
                <label className="flex items-start gap-3 rounded-xl border border-violet-200 bg-violet-50 p-4 text-sm text-[#274249]">
                  <input
                    type="checkbox"
                    checked={newUser.superAdmin}
                    onChange={(e) => setNewUser({ ...newUser, superAdmin: e.target.checked, role: e.target.checked ? 'admin' : newUser.role })}
                    className="mt-1"
                  />
                  <span><strong className="block text-violet-800">Platform super administrator</strong><span className="mt-1 block font-normal leading-5 text-violet-700">Can manage customer branding and grant or revoke super-administrator access. This also assigns the Admin role.</span></span>
                </label>
              )}
            </div>
            <div className="mt-7 flex justify-end gap-3">
              <button type="button" onClick={closeCreate} disabled={creating} className="rounded-full border border-[#CAD2D5] px-5 py-2.5 text-sm font-semibold text-[#35565C]">Cancel</button>
              <button type="submit" disabled={creating || !newUserOrgVerdict.canSubmit} className="flex items-center gap-2 rounded-full bg-[var(--brand-primary)] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#005557] disabled:opacity-60">
                <UserPlus className="h-4 w-4" /> {creating ? 'Creating and sending…' : 'Create and email user'}
              </button>
            </div>
          </form>
        </div>
      )}

      {orgDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#001820]/55 p-4" role="dialog" aria-modal="true" aria-labelledby="org-dialog-title">
          <div className="w-full max-w-lg rounded-3xl bg-white p-7 shadow-2xl">
            <div className="mb-6 flex items-start justify-between">
              <div>
                <p className="mb-1 text-xs font-bold uppercase tracking-[0.14em] text-[var(--brand-primary)]">Organisation</p>
                <h2 id="org-dialog-title" className="text-2xl font-semibold text-[#001820]">
                  {orgDialog.mode === 'approve'
                    ? 'Approve this account'
                    : orgDialog.mode === 'finish'
                      ? 'Finish organisation setup'
                      : 'Choose an organisation'}
                </h2>
                <p className="mt-2 text-sm leading-6 text-[#5F696C]">
                  {orgDialog.user.displayName ? `${orgDialog.user.displayName} (${orgDialog.user.email})` : orgDialog.user.email}
                </p>
                <p className="mt-2 text-sm leading-6 text-[#5F696C]">
                  {orgDialog.mode === 'approve'
                    ? 'Every approved account belongs to an organisation. The account can only see the studies of the organisation you choose.'
                    : orgDialog.mode === 'finish'
                      ? 'This account is already a member of this organisation, but its sign-in was not updated, so it cannot see the organisation\'s studies yet. Finishing updates its sign-in. The membership itself does not change.'
                      : 'This account is approved but belongs to no organisation, so it cannot see any studies. Choose the organisation it belongs to.'}
                </p>
              </div>
              <button type="button" onClick={() => setOrgDialog(null)} disabled={orgSaving} className="rounded-full p-2 text-[#5F696C] hover:bg-[#F1F4F5]" aria-label="Close">
                <X className="h-5 w-5" />
              </button>
            </div>
            <OrganisationPicker
              email={orgDialog.user.email}
              orgs={orgs}
              orgsLoading={orgsLoading}
              value={orgChoice}
              onChange={setOrgChoice}
              acknowledged={orgAck}
              onAcknowledgedChange={setOrgAck}
              disabled={orgSaving}
            />
            <div className="mt-7 flex justify-end gap-3">
              <button type="button" onClick={() => setOrgDialog(null)} disabled={orgSaving} className="rounded-full border border-[#CAD2D5] px-5 py-2.5 text-sm font-semibold text-[#35565C]">Cancel</button>
              <button
                type="button"
                onClick={submitOrgDialog}
                disabled={orgSaving || !orgDialogVerdict?.canSubmit}
                className="rounded-full bg-[var(--brand-primary)] px-5 py-2.5 text-sm font-bold text-white hover:bg-[#005557] disabled:opacity-60"
              >
                {orgSaving
                  ? 'Saving...'
                  : orgDialog.mode === 'approve'
                    ? 'Approve'
                    : orgDialog.mode === 'finish'
                      ? 'Finish setup'
                      : 'Save organisation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
